import * as React from 'react'

import useI18n from '@store/i18n/useI18n'
import styled from '@theme/styled-components'

import FileDropzone from '@components/dropzone/FileDropzone'
import CsvPreview from '@components/csvPreview/CsvPreview'
import Swal from 'sweetalert2'
import Button from '@components/button/button'
import apis from '@app/api/api'

import Tooltip from './element/tooltip'

const VIEW_MODE = ['dropzone', 'initial', 'response'] as const

interface Props {
  navigation: Navigation
}

const Affectation = ({ navigation }: Props) => {
  const [file, setFile] = React.useState<Blob>()
  const [fileString, setFileString] = React.useState<string | null>()
  const [responseFile, setResponseFile] = React.useState<string>()
  const [selectedViewMode, setSelectedViewMode] = React.useState<typeof VIEW_MODE[number]>(VIEW_MODE[0])
  const [loading, setLoading] = React.useState(false)

  const i18n = useI18n()

  React.useEffect(() => {
    if (file) {
      const reader = new window.FileReader()
      reader.readAsText(file)
      reader.onloadend = () => {
        const source = reader.result
        setFileString(source as string)
      }
      reader.onerror = () => {
        setFileString('error')
      }
    }
  }, [file])

  const launchAlert = (type: 'success' | 'error') => {
    Swal.fire({
      title: i18n.t(`components.adminData.jobtype.alert.${type}.title`),
      text: i18n.t(`components.adminData.jobtype.alert.${type}.text`),
      icon: type,
      showCancelButton: false,
      confirmButtonColor: '#3A8641',
      confirmButtonText: i18n.t(`components.adminData.jobtype.alert.${type}.confirm`),
    })
  }

  const launchJob = () => {
    setLoading(true)
    const reader = new window.FileReader()
    if (file) {
      reader.readAsDataURL(file)
    }
    reader.onloadend = () => {
      const source = reader.result
      apis.admin.business
        .import(source)
        .then((res) => {
          setResponseFile(res as string)
          launchAlert('success')
          setLoading(false)
        })
        .catch(() => {
          launchAlert('error')
          setLoading(false)
        })
    }
    reader.onerror = () => {
      launchAlert('error')
    }
  }

  const ViewModeSelection = () => {
    return (
      <SelectionContainer>
        {VIEW_MODE.map((itm, idx) => {
          const noFile = (idx === 1 && !fileString) || (idx === 2 && !responseFile)
          return (
            <SelectionItem
              key={`${itm} - ${idx}`}
              selected={itm === selectedViewMode}
              type="tertiary"
              onClick={() => {
                if (
                  selectedViewMode !== itm &&
                  (idx === 0 || (idx === 1 && fileString) || (idx === 2 && responseFile))
                ) {
                  setSelectedViewMode(itm)
                }
              }}
              disabled={noFile}
              first={idx === 0}
              last={idx === VIEW_MODE.length - 1}>
              <SelectionLibelle selected={itm === selectedViewMode} type="tertiary" disabled={noFile}>
                {i18n.t(`components.adminData.jobtype.selection.viewmode.${itm}`)}
              </SelectionLibelle>
            </SelectionItem>
          )
        })}
      </SelectionContainer>
    )
  }

  const Content = () => {
    return (
      <>
        <FileUploadContent>
          <ButtonContainer>
            <Button
              title={i18n.t('components.adminData.jobtype.launchjob')}
              onClick={launchJob}
              type="action"
              status={loading ? 'loading' : file ? 'active' : 'disabled'}
            />
          </ButtonContainer>
          <ViewModeSelection />
        </FileUploadContent>
        {selectedViewMode === 'dropzone' ? (
          <FileDropzone file={file} setFile={setFile} acceptedTypes={['text/csv']} />
        ) : selectedViewMode === 'initial' && fileString ? (
          <CsvPreview csv={fileString} error={fileString === 'error'} />
        ) : (
          <CsvPreview csv={responseFile} outlinederror="KO" outlinedsuccess="OK" />
        )}
      </>
    )
  }

  return (
    <>
      <SectionTitleContainer>
        {i18n.t('components.adminData.sectionTitles.affectation')}
        <Tooltip navigation={navigation} />
      </SectionTitleContainer>
      <Content />
    </>
  )
}

export default Affectation

const SectionTitleContainer = styled('div')`
  ${(props) => props.theme.fonts.sectionTitle};
  margin: 10px;
  display: flex;
  align-items: center;
  margin-top: 0px;
`

const RADIUS = 10
const SelectionContainer = styled('div')`
  display: flex;
  flex-direction: row;
  flex: 1;
  height: 40px;
  max-height: 40px;
  border-radius: ${RADIUS}px;
  background-color: white;
  box-shadow: 1px 1px 2px 1px #2224;
`
const SelectionItem = styled('div')<{
  selected: boolean
  first: boolean
  last: boolean
  type: 'primary' | 'secondary' | 'tertiary'
  disabled?: boolean
}>`
  display: flex;
  flex: 1;
  height: 40px;
  max-height: 40px;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.selected
      ? props.type === 'primary'
        ? props.theme.colors.selectionItemSelected
        : props.type === 'secondary'
        ? props.theme.colors.selectionSecondaryItemSelected
        : props.theme.colors.selectionTertiaryItemSelected
      : props.type === 'primary'
      ? props.theme.colors.selectionItemUnselected
      : props.type === 'secondary'
      ? props.theme.colors.selectionSecondaryItemUnselected
      : props.theme.colors.selectionTertiaryItemUnselected};

  ${(props) => props.last && `border-radius: 0 ${RADIUS}px ${RADIUS}px 0;`}
  ${(props) => props.first && `border-radius: ${RADIUS}px 0 0 ${RADIUS}px;`}
  cursor: pointer;
  ${(props) => props.disabled && `background-color: ${props.theme.colors.disabledButton}; cursor: unset; `}
`
const SelectionLibelle = styled('div')<{
  selected: boolean
  type: 'primary' | 'secondary' | 'tertiary'
  disabled?: boolean
}>`
  ${(props) => props.theme.fonts.label};
  color: ${(props) =>
    props.selected
      ? props.type === 'primary'
        ? props.theme.colors.selectionLibelleSelected
        : props.type === 'secondary'
        ? props.theme.colors.selectionSecondaryLibelleSelected
        : props.theme.colors.selectionTertiaryLibelleSelected
      : props.type === 'primary'
      ? props.theme.colors.selectionLibelleUnselected
      : props.type === 'secondary'
      ? props.theme.colors.selectionSecondaryLibelleUnselected
      : props.theme.colors.selectionTertiaryLibelleUnselected};
  ${(props) => props.selected && 'font-weight: bold;'}
  ${(props) => props.disabled && `color: ${props.theme.colors.classicButton};`}
`

const FileUploadContent = styled('div')`
  margin: 25px;
  display: flex;
  flex-direction: column;
`

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: right;
  margin-bottom: 16px;
`
